/**
 * Konva Map Controller
 *
 * Thin Stimulus adapter for the interactive map canvas on DM and player views.
 *
 * The feature implementation is split into plain JavaScript method groups under
 * `app/frontend/lib/konva_map/`. Keep the Stimulus targets/values/actions here
 * stable because Rails views bind to them directly with `data-konva-map-*` and
 * `data-action="...->konva-map#..."` attributes.
 *
 * @see docs/KONVA_MAP_CONTROLLER.md for full architecture documentation.
 * @see docs/KONVA_MAP_SKILL.md for the quick agent-oriented feature map.
 */
import { CharacterTokenMethods } from '../lib/konva_map/character_token_methods';
import { CharacterMovementMethods } from '../lib/konva_map/character_movement_methods';
import { Controller } from '@hotwired/stimulus';
import { DRAW_WIDTH, ERASE_RADIUS } from '../lib/konva_map/constants';
import { AreaEffectMethods } from '../lib/konva_map/area_effect_methods';
import { ActionsMethods } from '../lib/konva_map/actions_methods';
import { FogMethods } from '../lib/konva_map/fog_methods';
import { LifecycleMethods } from '../lib/konva_map/lifecycle_methods';
import { EffectRotationMethods } from '../lib/konva_map/effect_rotation_methods';
import { PersistenceMethods } from '../lib/konva_map/persistence_methods';
import { PointerMethods } from '../lib/konva_map/pointer_methods';
import { PositionManagerMethods } from '../lib/konva_map/position_manager_methods';
import { ShapePopoverMethods } from '../lib/konva_map/shape_popover_methods';
import { ShapeMethods } from '../lib/konva_map/shape_methods';
import { ShapeAnimationMethods } from '../lib/konva_map/shape_animation_methods';
import { StageMethods } from '../lib/konva_map/stage_methods';
import { ToolbarMethods } from '../lib/konva_map/toolbar_methods';
import { UtilityMethods } from '../lib/konva_map/utility_methods';
import { ZoomMethods } from '../lib/konva_map/zoom_methods';
import { TouchGestureMethods } from '../lib/konva_map/touch_gesture_methods';
import { MobileEditorMethods } from '../lib/konva_map/mobile_editor_methods';
import { WorkspaceMethods } from '../lib/konva_map/workspace_methods';
import { HistoryMethods } from '../lib/konva_map/history_methods';
import { MonitorMethods } from '../lib/konva_map/monitor_methods';
import { GridSettingsMethods } from '../lib/konva_map/grid_settings_methods';
class KonvaMapController extends Controller {
  static targets = [
    'gridStatus',
    'gridOverlayToggle',
    'gridCalibrationPanel',
    'gridCalibrationMessage',
    'toolRail',
    'autosave',
    'undo',
    'redo',
    'toolLabels',
    'modeButton',
    'fullscreenButton',
    'layersPanel',
    'layersButton',
    'positionFields',
    'workspaceInstruction',
    'placementControls',
    'selectionToolbar',
    'selectionName',
    'selectionMove',
    'positionMonitor',
    'monitorBezel',
    'monitorRotation',
    'monitorMissing',
    'editor',
    'characterTool',
    'characterGridNotice',
    'mobileControls',
    'mobileZoomControls',
    'mobileExplore',
    'mobileTools',
    'mobileShapeAction',
    'mobileBrush',
    'mobileBrushLabel',
    'mobileSaveNotice',
    'mobileInstruction',
    'mobileGridNotice',
    'placeEffect',
    'cancelEdit',
    'editSelected',
    'saveStatus',
    'unsavedNotice',
    'unsavedCount',
    'imagePlaceholder',
    'retrySave',
    'backgroundStatus',
    'container', // Konva mounts its <canvas> elements here
    'mapNameOverlay', // read-only label over the canvas showing the current map name
    'mapTitle',
    'mapTitleDetails',
    'mapTitleInstruction',
    'toolbar', // (reserved) toolbar container
    'cursor', // receives style.cursor changes (same element as container)
    'viewport', // overflow:hidden wrapper that defines the visible canvas area
    'zoomDisplay', // dropdown toggle button — text updated to current zoom %
    'dragBtn', // hand-icon button — gets .active class in drag mode
    'selectBtn', // mouse-pointer button — gets .active class in select mode
    'bottomZoomControls', // bottom toolbar zoom/reset controls hidden in position-manager mode
    'bottomToolbarDivider', // divider before move-map/move-shapes buttons
    'fogBrushPopover', // shared slider beside the active fog brush
    'fogBrushAnchor', // pencil and eraser buttons hosting the shared slider
    'fogBrushSizeInput', // range input for active fog pencil/eraser size
    'positionBottomHint', // helper text shown in the bottom toolbar in position-manager mode
    'positionViewport', // dashed game-screen viewport indicator shown in position-manager mode
    'positionCharactersViewport', // dashed character-panel viewport indicator shown beside the game viewport
    'positionViewportLabel', // viewport dimensions label(s), in the overlay and toolbar
    'positionPosXLabel', // overlay X coordinate label
    'positionPosYLabel', // overlay Y coordinate label
    'positionZoomInput', // position-manager zoom input (image_zoom)
    'positionPosXInput', // position-manager X input (image_position_x)
    'positionPosYInput', // position-manager Y input (image_position_y)
    'positionZoomDisplay', // position-manager zoom percentage display
    'shapePopover',
    'shapeName',
    'shapeColor',
    'shapeSize',
    'shapeSizeControl',
    'shapeSizeLabel',
    'shapeDirection',
    'shapeDirectionDial',
    'characterMovement',
    'undoCharacterMove',
    'characterMovePanel',
    'characterMoveName',
    'characterMoveStatus',
    'confirmCharacterMove',
    'shapesList', // sidebar container for the shapes/layers list
    'shapeEditModal' // UTMR modal content populated from the live shape state
  ];
  static values = {
    workspace: Boolean,
    positionGameVisible: { type: Boolean, default: true },
    positionScreenWidth: { type: Number, default: 1920 },
    positionScreenHeight: { type: Number, default: 1080 },
    positionCharactersVisible: Boolean,
    positionDisplayLabel: { type: String, default: 'Default viewport' },
    requestedCharacter: Object,
    player: Boolean,
    userId: Number,
    stateUrl: String,
    canvasRevision: Number,
    imageContentType: String,
    hidden: Boolean,
    detectGridUrl: String,
    gridEnabled: Boolean,
    gridHidden: Boolean,
    gridDetectionApproximate: Boolean,
    gridOffsetX: { type: Number, default: 0 },
    gridOffsetY: { type: Number, default: 0 },
    gridSize: { type: Number, default: 50 },
    gridWidth: { type: Number, default: 1 },
    gridColor: { type: String, default: '#000000' },
    previewUrl: String,
    imageWidth: Number,
    imageHeight: Number,
    imageUrl: String, // ActiveStorage URL for the map image
    saveUrl: String, // URL of the save_canvas endpoint
    shapeModalUrl: String, // URL of the UTMR layer editor
    canvasJson: String, // serialised fog+shapes JSON embedded in the HTML
    readonly: Boolean, // true on the game-screen display — disables tools and saving
    reinitialize: String, // changing this value triggers a full re-init
    fogOpacity: { type: Number, default: 1.0 }, // CSS opacity on the fog layer canvas (0.65 in the map editor)
    positionUpdateUrl: String, // regular game_map update endpoint for image_zoom / image_position_x / image_position_y
    positionZoom: { type: Number, default: 0 },
    positionPosX: { type: Number, default: 0 },
    positionPosY: { type: Number, default: 0 },
    positionWindowWidth: { type: Number, default: 1920 },
    positionWindowHeight: { type: Number, default: 1080 },
    positionCharactersWidth: { type: Number, default: 0 },
    positionCharactersHeight: { type: Number, default: 0 },
    characterPlacement: { type: String, default: 'top' },
    effectiveCharacterPlacement: { type: String, default: 'top' },
    gameViewRotation: { type: String, default: 'zero' },
    drawBrushSize: { type: Number, default: DRAW_WIDTH },
    eraseBrushSize: { type: Number, default: ERASE_RADIUS * 2 }
  };

  mapTitleTargetConnected() {
    this._syncMapTitle();
  }

  editorTargetConnected() {
    this._mobileViewportChanged?.();
  }

  mobileControlsTargetConnected() {
    this._mobileControlsObserver?.disconnect();
    this._mobileControlsObserver?.observe(this.mobileControlsTarget);
    if (this.hasMobileZoomControlsTarget) this._mobileControlsObserver?.observe(this.mobileZoomControlsTarget);
  }

  mobileZoomControlsTargetConnected() {
    this._mobileControlsObserver?.observe(this.mobileZoomControlsTarget);
  }

  shapePopoverTargetConnected() {
    this._refreshShapePopover();
  }

  shapeEditModalTargetConnected() {
    this._refreshEditModal();
  }

  containerTargetConnected() {
    if (this._mapConnected && !this._stage) this._queueMapReload();
  }

  shapesListTargetConnected() {
    if (this._stage && this._shapes) this._updateShapesList();
  }

  shapesListTargetDisconnected() {
    if (this._stage && this._shapes) this._updateShapesList();
  }
}
Object.assign(
  KonvaMapController.prototype,
  LifecycleMethods,
  ActionsMethods,
  StageMethods,
  ToolbarMethods,
  PointerMethods,
  PositionManagerMethods,
  FogMethods,
  ShapeMethods,
  ShapeAnimationMethods,
  ShapePopoverMethods,
  AreaEffectMethods,
  CharacterTokenMethods,
  CharacterMovementMethods,
  EffectRotationMethods,
  ZoomMethods,
  TouchGestureMethods,
  MobileEditorMethods,
  PersistenceMethods,
  UtilityMethods,
  WorkspaceMethods,
  HistoryMethods,
  MonitorMethods,
  GridSettingsMethods
);
export default KonvaMapController;
